import React, { useState, useEffect, useCallback } from 'react';
import { CHECKBOX_LABEL_POSITIONS } from 'cdk-radial';
import { IHeaderParams } from 'ag-grid-community';
import StyledCustomHeaderCheckbox from './styled';

const CustomHeaderCheckbox = ({
  api,
  displayName
}: IHeaderParams): JSX.Element => {
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const headerText = displayName || '';

  const getPageIndices = useCallback(() => {
    if (!api) return { startIndex: 0, endIndex: 0 };
    const currentPage = api.paginationGetCurrentPage();
    const pageSize = api.paginationGetPageSize();
    const displayedRowCount = api.getDisplayedRowCount();
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, displayedRowCount);
    return { startIndex, endIndex };
  }, [api]);

  const updateCheckbox = useCallback(() => {
    if (!api) return;
    const { startIndex, endIndex } = getPageIndices();
    if (endIndex - startIndex <= 0) {
      setChecked(false);
      setIndeterminate(false);
      return;
    }

    let allSelected = true;
    let someSelected = false;
    for (let i = startIndex; i < endIndex; i += 1) {
      const rowNode = api.getDisplayedRowAtIndex(i);
      if (rowNode) {
        if (rowNode.isSelected()) {
          someSelected = true;
        } else {
          allSelected = false;
        }
      }
    }
    setChecked(allSelected);
    setIndeterminate(someSelected && !allSelected);
  }, [api, getPageIndices]);

  useEffect(() => {
    if (!api) return undefined;
    const updateHandler = () => updateCheckbox();
    api.addEventListener('selectionChanged', updateHandler);
    api.addEventListener('paginationChanged', updateHandler);
    updateCheckbox();
    return () => {
      api.removeEventListener('selectionChanged', updateHandler);
      api.removeEventListener('paginationChanged', updateHandler);
    };
  }, [api, updateCheckbox]);

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIndeterminate(false);
    setChecked(isChecked);
    if (!api) return;
    const { startIndex, endIndex } = getPageIndices();
    for (let i = startIndex; i < endIndex; i += 1) {
      const rowNode = api.getDisplayedRowAtIndex(i);
      if (rowNode) {
        rowNode.setSelected(isChecked);
      }
    }
  };

  return (
    <StyledCustomHeaderCheckbox
      checked={checked || indeterminate}
      indeterminate={indeterminate}
      labelPosition={CHECKBOX_LABEL_POSITIONS.AFTER}
      onChange={onCheckboxChange}
      label={headerText}
    />
  );
};

export default CustomHeaderCheckbox;
